// ============================================================================
// PWA INSTALL SERVICE (singleton)
// ----------------------------------------------------------------------------
// The browser fires `beforeinstallprompt` exactly once and very early in the
// page lifecycle. If each component registers its own listener on mount, the
// event may already have fired and the install option silently disappears.
//
// This module registers a single listener as soon as it is imported (we import
// it from index.js) and keeps the deferred prompt in a module-level variable so
// any component can request installation at any time via the usePWAInstall hook.
// ============================================================================

let deferredPrompt = null;
let isInstalled = false;
const listeners = new Set();

const isBrowser = typeof window !== 'undefined';

// Detect iOS / iPadOS (these never fire beforeinstallprompt and need manual
// "Add to Home Screen" instructions instead).
const detectIOS = () => {
  if (!isBrowser) return false;
  const ua = window.navigator.userAgent || '';
  const iOSDevice = /iPad|iPhone|iPod/i.test(ua);
  // iPadOS 13+ masquerades as macOS, so also check touch capability.
  const iPadOS = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
};

const detectStandalone = () => {
  if (!isBrowser) return false;
  const mediaStandalone =
    window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = window.navigator.standalone === true;
  return Boolean(mediaStandalone || iosStandalone);
};

const isIOS = detectIOS();

const notify = () => {
  const snapshot = getState();
  listeners.forEach((cb) => {
    try {
      cb(snapshot);
    } catch (err) {
      // Never let one bad subscriber break the rest.
      console.warn('PWA install listener error:', err);
    }
  });
};

export const getState = () => ({
  // True when the native install prompt is available to be shown.
  canPrompt: Boolean(deferredPrompt),
  // True once the app is running as an installed PWA (or was just installed).
  isInstalled: isInstalled || detectStandalone(),
  // True when running inside the installed standalone window.
  isStandalone: detectStandalone(),
  // True on iOS/iPadOS where install must be done manually via Share menu.
  isIOS,
  // The app can be installed in some way (native prompt OR iOS manual flow),
  // and it is not already installed.
  installable:
    !(isInstalled || detectStandalone()) && (Boolean(deferredPrompt) || isIOS),
});

export const subscribe = (callback) => {
  listeners.add(callback);
  // Push the current state immediately so late subscribers are in sync.
  callback(getState());
  return () => listeners.delete(callback);
};

// Trigger the native install prompt. Returns the user choice outcome string
// ('accepted' | 'dismissed' | 'unavailable').
export const promptInstall = async () => {
  if (!deferredPrompt) return 'unavailable';
  try {
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    notify();
    return choice?.outcome || 'dismissed';
  } catch (err) {
    console.warn('PWA: error while showing install prompt:', err);
    deferredPrompt = null;
    notify();
    return 'unavailable';
  }
};

if (isBrowser) {
  // Establish initial installed state.
  isInstalled = detectStandalone();

  window.addEventListener('beforeinstallprompt', (e) => {
    // Stop Chrome's mini-infobar so we can present our own UI.
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    isInstalled = true;
    notify();
  });

  // Keep state fresh if the user switches to/from standalone mode.
  if (window.matchMedia) {
    const mq = window.matchMedia('(display-mode: standalone)');
    const onChange = () => {
      isInstalled = detectStandalone();
      notify();
    };
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
    } else if (mq.addListener) {
      mq.addListener(onChange);
    }
  }
}
